"use client"

import { useEffect, useRef } from "react"
import { mouseStore } from "../mouseStore"

interface UseMagneticOptions {
  /** Pull strength: 0 = no pull, 1 = cursor snaps to element center */
  strength?: number
  /** Distance in px at which the pull begins */
  distance?: number
}

/**
 * Attaches a magnetic pull effect to an element: when the cursor comes within
 * `distance` pixels of the element's center, the element shifts toward the cursor.
 *
 * Uses the shared mouseStore — zero React re-renders in the hot path.
 *
 * Usage:
 *   const ref = useMagnetic<HTMLButtonElement>()
 *   return <button ref={ref}>Hover me</button>
 */
export function useMagnetic<T extends HTMLElement = HTMLElement>({
  strength = 0.35,
  distance = 80,
}: UseMagneticOptions = {}) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const unsub = mouseStore.subscribe((x, y) => {
      const rect = el.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const dx = x - centerX
      const dy = y - centerY
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < distance) {
        const pull = (1 - dist / distance) * strength
        el.style.transform = `translate(${dx * pull}px, ${dy * pull}px)`
        el.style.transition = "transform 0.08s linear"
      } else {
        el.style.transform = "translate(0px, 0px)"
        el.style.transition = "transform 0.4s ease"
      }
    })

    return () => {
      unsub()
      if (ref.current) {
        ref.current.style.transform = ""
        ref.current.style.transition = ""
      }
    }
  }, [strength, distance])

  return ref
}
